/**
 * @fileoverview Shared consent persistence for the privacy choices page and banner.
 * @description Centralizes localStorage writes, Consent Mode updates, attribution
 * purge on opt-out, and the edge-readable analytics consent mirror cookie.
 *
 * @module lib/analytics/consent-preferences
 */

import {
  CONSENT_STORAGE_KEY,
  notifyConsentChanged,
} from "./consent"
import {
  ANALYTICS_COOKIE_MAX_AGE_SEC,
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
  CONSENT_ANALYTICS_MIRROR_COOKIE,
} from "./cookie-names"
import { trackConsentUpdate } from "~/lib/gtag"
import type { ConsentModeSettings, ConsentPreferences } from "~/types/consent"

export { CONSENT_STORAGE_KEY, CONSENT_ANALYTICS_MIRROR_COOKIE }

const CONSENT_VERSION = 1

/**
 * Validates consent preferences structure from localStorage.
 *
 * @param {unknown} obj - Parsed JSON value.
 * @returns {obj is ConsentPreferences} Whether the value is valid.
 */
function isValidConsentPreferences(obj: unknown): obj is ConsentPreferences {
  if (!obj || typeof obj !== "object") return false

  const candidate = obj as Record<string, unknown>

  return (
    typeof candidate.version === "number" &&
    typeof candidate.updatedAt === "string" &&
    typeof candidate.strictlyNecessary === "boolean" &&
    typeof candidate.analytics === "boolean" &&
    typeof candidate.functional === "boolean" &&
    typeof candidate.ads === "boolean" &&
    typeof candidate.doNotSellOrShare === "boolean" &&
    typeof candidate.gpcDetected === "boolean"
  )
}

/**
 * Whether the visitor has a persisted consent choice (localStorage or cookie fallback).
 *
 * Uses the explicit grant cookie when localStorage is unavailable — the mirror alone
 * is not enough on EEA first visits (bootstrap seeds mirror=0 before any choice).
 *
 * @returns {boolean} True when a saved accept/reject choice exists.
 */
export function hasPersistedConsentChoice(): boolean {
  if (readStoredConsentPreferences() !== null) return true
  if (typeof document === "undefined") return false
  try {
    const header = document.cookie
    return (
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`) ||
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=0`)
    )
  } catch {
    return false
  }
}

/**
 * Reads validated consent preferences from localStorage, clearing corrupt data.
 *
 * @returns {ConsentPreferences | null} Valid current-version prefs or null.
 */
export function readStoredConsentPreferences(): ConsentPreferences | null {
  if (typeof window === "undefined") return null

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!isValidConsentPreferences(parsed) || parsed.version !== CONSENT_VERSION) {
      try {
        localStorage.removeItem(CONSENT_STORAGE_KEY)
      } catch {
        // ignore — storage may be readable but not writable
      }
      return null
    }

    return parsed
  } catch {
    // getItem/parse failed (storage disabled) — skip removeItem; it can throw too
    // and would prevent ConsentBanner from showing for blocked-storage EEA visits.
    return null
  }
}

/**
 * Detects Global Privacy Control in the current browser.
 *
 * @returns {boolean} True when GPC is enabled.
 */
export function detectGlobalPrivacyControl(): boolean {
  return typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
}

/**
 * Writes the analytics consent mirror cookie for edge middleware.
 *
 * @param {boolean} analyticsAllowed - Whether analytics consent is granted.
 */
export function setConsentAnalyticsMirrorCookie(analyticsAllowed: boolean): void {
  if (typeof document === "undefined") return
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${CONSENT_ANALYTICS_MIRROR_COOKIE}=${analyticsAllowed ? "1" : "0"}; Path=/; Max-Age=${ANALYTICS_COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`
}

/**
 * Writes the explicit analytics grant cookie for edge middleware (EEA opt-in).
 *
 * @param {boolean} explicitGrant - Whether the visitor explicitly opted in.
 */
export function setConsentAnalyticsExplicitCookie(explicitGrant: boolean): void {
  if (typeof document === "undefined") return
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=${explicitGrant ? "1" : "0"}; Path=/; Max-Age=${ANALYTICS_COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`
}

/**
 * Replays stored preferences into edge-readable consent cookies (mirror + explicit).
 *
 * Used when localStorage holds a saved choice but cookies expired — e.g. a
 * returning visitor landing directly on `/privacy/choices`.
 *
 * @returns {void}
 */
export function replayEdgeConsentCookiesFromStoredPreferences(): void {
  const prefs = readStoredConsentPreferences()
  if (!prefs) return
  setConsentAnalyticsMirrorCookie(prefs.analytics)
  setConsentAnalyticsExplicitCookie(prefs.analytics)
}

/**
 * Applies consent preferences to Google Consent Mode and records `consent_update`.
 *
 * @param {ConsentPreferences} preferences - Saved preferences.
 */
export function applyConsentModeFromPreferences(preferences: ConsentPreferences): void {
  if (typeof window === "undefined") return
  const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag !== "function") return

  const grantedDenied = (allowed: boolean): "granted" | "denied" =>
    allowed ? "granted" : "denied"

  const gpc = detectGlobalPrivacyControl()
  const adsAllowed = preferences.ads && !preferences.doNotSellOrShare && !gpc

  const consentSettings: ConsentModeSettings = {
    analytics_storage: grantedDenied(preferences.analytics),
    functionality_storage: grantedDenied(preferences.functional),
    security_storage: "granted",
    ad_storage: grantedDenied(adsAllowed),
    ad_user_data: grantedDenied(adsAllowed),
    ad_personalization: grantedDenied(adsAllowed),
    personalization_storage: grantedDenied(adsAllowed),
  }

  gtag("consent", "update", consentSettings)

  trackConsentUpdate({
    analytics_consent: preferences.analytics,
    functional_consent: preferences.functional,
    ads_consent: adsAllowed,
    do_not_sell_share: preferences.doNotSellOrShare,
    gpc_detected: gpc,
  })
}

/**
 * Purges site-intelligence session stores (journey + engagement counters).
 * Dynamic imports avoid a static attribution ↔ session-journey cycle.
 *
 * @returns {void}
 */
export function purgeSiteIntelligenceSessionStores(): void {
  void import("~/lib/site-intelligence/session-journey")
    .then((m) => m.resetSessionJourney())
    .catch(() => undefined)
  void import("~/lib/site-intelligence/session-engagement")
    .then((m) => m.resetSessionEngagement())
    .catch(() => undefined)
}

/**
 * Persists consent preferences and synchronizes mirrors/side effects.
 *
 * @param {ConsentPreferences} preferences - Preferences to store.
 */
export function persistConsentPreferences(preferences: ConsentPreferences): void {
  if (typeof window === "undefined") return

  setConsentAnalyticsMirrorCookie(preferences.analytics)
  setConsentAnalyticsExplicitCookie(preferences.analytics)
  notifyConsentChanged()

  if (!preferences.analytics) {
    void import("./attribution")
      .then((m) => m.clearStoredAttribution())
      .catch(() => undefined)
    purgeSiteIntelligenceSessionStores()
  }

  applyConsentModeFromPreferences(preferences)

  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences))
  } catch {
    // Storage unavailable — consent side effects above still applied best-effort.
  }
}

/**
 * Builds default EEA-first-visit preferences (analytics denied until accept).
 *
 * @param {boolean} analytics - Analytics consent flag.
 * @returns {ConsentPreferences} Preferences object ready to persist.
 */
export function buildConsentPreferences(analytics: boolean): ConsentPreferences {
  const gpc = detectGlobalPrivacyControl()
  return {
    version: 1,
    updatedAt: new Date().toISOString(),
    analytics,
    functional: true,
    ads: false,
    strictlyNecessary: true,
    doNotSellOrShare: gpc,
    gpcDetected: gpc,
  }
}
